import { ReactElement } from "react";
import BaseChart, { BaseChartEffect } from "./BaseChart";
import { getChartAPI as getActivityChartAPI } from "../Activity/ActivityChartApi";
import { getChartAPI as getActivityPressureChartAPI } from "../Activity/ActivityPressureChartApi";
import { getChartAPI as getActivityFlowRateChartAPI } from "../Activity/ActivityFlowRateChartApi";
import { getChartAPI as getActivityAssetChartAPI } from "../Activity/ActivityAssetChartApi";
import { getChartAPI as getActivityAssetPressureChartAPI } from "../Activity/ActivityAssetPressureChartApi";
import { getChartAPI as getActivityAssetFlowRateChartAPI } from "../Activity/ActivityAssetFlowRateChartApi";
import { getChartAPI as getComplianceChartAPI } from "../Compliance/ComplianceChartApi";
import { getChartAPI as getDiagnosticsChartAPI } from "../Diagnostics/DiagnosticsChartApi";
import { getChartAPI as getStatusChartAPI } from "../Status/StatusChartApi";
import { getChartAPI as getTimelineChartAPI } from "../Timeline/TimelineChartApi";
import { getChartAPI as getFuelBurnChartAPI } from "../FuelBurn/FuelBurnChartApi";

export type ChartType = 
  | 'activity'
  | 'activity-pressure'
  | 'activity-flow-rate'
  | 'activity-asset'
  | 'activity-asset-pressure'
  | 'activity-asset-flow-rate'
  | 'compliance'
  | 'diagnostics'
  | 'status'
  | 'timeline'
  | 'fuel-burn';

interface BaseChartProps {
  chartData: any;
  isSimpleChart?: boolean;
  chartParentHeight?: number;
  addHitTestRadius?: boolean;
  isFullscreen?: boolean;
  greasePumpData?: {
    serialNo: string;
    channelNo: number;
    timestamps: number[];
    values: number[];
  };
  visibleDataSeries?: {
    pressureMinMax?: boolean;
    pressureMean?: boolean;
    temperature?: boolean;
    volume?: boolean;
    flowRate?: boolean;
  };
  showStatus?: boolean;
}

interface ActivityChartProps extends BaseChartProps {
  chartType: 'activity';
}

interface ActivityPressureChartProps extends BaseChartProps {
  chartType: 'activity-pressure';
}

interface ActivityFlowRateChartProps extends BaseChartProps {
  chartType: 'activity-flow-rate';
}

interface ActivityAssetChartProps extends BaseChartProps {
  chartType: 'activity-asset';
}

interface ActivityAssetPressureChartProps extends BaseChartProps {
  chartType: 'activity-asset-pressure';
  visibleDataSeries: {
    pressureMinMax: boolean;
    pressureMean: boolean;
    temperature: boolean;
    volume: boolean;
  };
}

interface ActivityAssetFlowRateChartProps extends BaseChartProps {
  chartType: 'activity-asset-flow-rate';
  visibleDataSeries: {
    volume?: boolean;
    volumeTemperatureCompensated?: boolean;
    flowRate?: boolean;
    flowRateTemperatureCompensated?: boolean;
    temperature?: boolean;
  };
  isFullscreen?: boolean;
}

interface ComplianceChartProps extends BaseChartProps {
  chartType: 'compliance';
  isSimpleChart: boolean;
}

interface DiagnosticsChartProps extends BaseChartProps {
  chartType: 'diagnostics';
  isSimpleChart: boolean;
}

interface StatusChartProps extends BaseChartProps {
  chartType: 'status';
  isFullscreen?: boolean;
  showStatus?: boolean;
}

interface TimelineChartProps extends BaseChartProps {
  chartType: 'timeline';
  isSimpleChart: boolean;
}

interface FuelBurnChartProps extends BaseChartProps {
  chartType: 'fuel-burn';
  isSimpleChart: boolean;
}

type ChartProps = 
  | ActivityChartProps
  | ActivityPressureChartProps
  | ActivityFlowRateChartProps
  | ActivityAssetChartProps
  | ActivityAssetPressureChartProps
  | ActivityAssetFlowRateChartProps
  | ComplianceChartProps
  | DiagnosticsChartProps
  | StatusChartProps
  | TimelineChartProps
  | FuelBurnChartProps;

export default function Chart(props: ChartProps): ReactElement {
  const getChartConfig = () => {
    switch (props.chartType) {
      case 'activity': {
        return {
          getChartAPI: getActivityChartAPI,
          chartKey: "activityChart",
          visibleSeriesStoreKey: "visibleSeriesActivity",
          setVisibleSeriesStoreKey: "setVisibleSeriesActivity",
          initialVisibleSeries: { VolumeDispensedId: true } as { [key: string]: boolean },
          defaultHeight: 500,
          showOverview: true,
          effects: [
            { type: 'changeChartType' as const, dependency: 'viewType' }
          ] as BaseChartEffect[]
        };
      }

      case 'activity-pressure': {
        return {
          getChartAPI: getActivityPressureChartAPI,
          chartKey: "activityAssetChart",
          visibleSeriesStoreKey: "visibleSeriesActivityPressure",
          setVisibleSeriesStoreKey: "setVisibleSeriesActivityPressure",
          initialVisibleSeries: {
            VolumeDispensedId: true,
            PressureMeanId: true,
            PressureMinMaxId: true,
            PressureTempId: true
          } as { [key: string]: boolean },
          useIsSinglePressureChart: true,
          defaultHeight: 500,
          showOverview: true,
          effects: [
            { type: 'changeChartType' as const, dependency: 'viewType' },
            { type: 'setIsSingleMode' as const, dependency: 'isSinglePressureChart' },
            { type: 'setAutoRange' as const, dependency: 'isAutoRange' }
          ] as BaseChartEffect[]
        };
      }

      case 'activity-flow-rate': {
        const p = props as ActivityFlowRateChartProps;
        return {
          getChartAPI: getActivityFlowRateChartAPI,
          chartKey: "activityFlowRateChart",
          visibleSeriesStoreKey: "visibleSeriesActivityFlowRate",
          setVisibleSeriesStoreKey: "setVisibleSeriesActivityFlowRate",
          initialVisibleSeries: {
            VolumeId: true,
            FlowRateId: true
          } as { [key: string]: boolean },
          defaultHeight: 500,
          showOverview: true,
          isFullscreen: p.isFullscreen,
          effects: [
            { type: 'changeChartType' as const, dependency: 'viewType' }
          ] as BaseChartEffect[]
        };
      }

      case 'activity-asset': {
        return {
          getChartAPI: getActivityAssetChartAPI,
          chartKey: "activityAssetChart",
          visibleSeriesStoreKey: "visibleSeriesAsset",
          setVisibleSeriesStoreKey: "setVisibleSeriesAsset",
          defaultHeight: 450,
          showOverview: true,
          wrapperClassName: "flex",
          additionalChartOptions: {
            isBarChartOn: true
          },
          effects: [
            { type: 'changeChartType' as const, dependency: 'visibleSeriesRef' },
            { type: 'setAutoRange' as const, dependency: 'isAutoRange' }
          ] as BaseChartEffect[]
        };
      }

      case 'activity-asset-pressure': {
        const p = props as ActivityAssetPressureChartProps;
        return {
          getChartAPI: getActivityAssetPressureChartAPI,
          chartKey: p.isFullscreen ? "asset-chart-fullscreen" : "asset-chart",
          visibleSeriesStoreKey: "visibleSeriesAsset",
          setVisibleSeriesStoreKey: "setVisibleSeriesAsset",
          defaultHeight: 450,
          showOverview: true,
          wrapperClassName: "flex",
          isFullscreen: p.isFullscreen,
          additionalChartOptions: {
            isBarChartOn: true,
            visibleDataSeries: p.visibleDataSeries
          },
          effects: [
            { type: 'changeChartType' as const, dependency: 'visibleSeriesRef' },
            { type: 'setAutoRange' as const, dependency: 'isAutoRange' },
            { 
              type: 'changeDataSeries' as const, 
              dependency: 'visibleDataSeries',
              getValue: (dep: string, options?: any) => options?.visibleDataSeries
            }
          ] as BaseChartEffect[]
        };
      }

      case 'activity-asset-flow-rate': {
        const p = props as ActivityAssetFlowRateChartProps;
        return {
          getChartAPI: getActivityAssetFlowRateChartAPI,
          chartKey: p.isFullscreen ? "asset-flow-rate-chart-fullscreen" : "asset-flow-rate-chart",
          visibleSeriesStoreKey: "visibleSeriesAsset",
          setVisibleSeriesStoreKey: "setVisibleSeriesAsset",
          defaultHeight: 450,
          showOverview: true,
          wrapperClassName: "flex",
          isFullscreen: p.isFullscreen,
          additionalChartOptions: {
            isBarChartOn: true,
            visibleDataSeries: p.visibleDataSeries
          },
          effects: [
            { type: 'changeChartType' as const, dependency: 'visibleSeriesRef' },
            { type: 'setAutoRange' as const, dependency: 'isAutoRange' },
            { 
              type: 'changeDataSeries' as const, 
              dependency: 'visibleDataSeries',
              getValue: (dep: string, options?: any) => options?.visibleDataSeries
            }
          ] as BaseChartEffect[]
        };
      }

      case 'compliance': {
        return {
          getChartAPI: getComplianceChartAPI,
          chartKey: "complianceChart",
          visibleSeriesStoreKey: "visibleSeriesCompliance",
          setVisibleSeriesStoreKey: "setVisibleSeriesCompliance",
          useIsThresholdsOn: true,
          defaultHeight: 450,
          showOverview: true,
          wrapperClassName: "flex",
          effects: [
            { type: 'changeChartType' as const, dependency: 'visibleSeriesRef' },
            { type: 'setAutoRange' as const, dependency: 'isAutoRange' },
            { type: 'showThresholds' as const, dependency: 'isThresholdsOn' }
          ] as BaseChartEffect[]
        };
      }

      case 'diagnostics': {
        const p = props as DiagnosticsChartProps;
        return {
          getChartAPI: getDiagnosticsChartAPI,
          chartKey: "diagnosticsChart",
          visibleSeriesStoreKey: "visibleSeriesDiagnostics",
          setVisibleSeriesStoreKey: "setVisibleSeriesDiagnostics",
          defaultHeight: 450,
          minHeight: "200px",
          showOverview: true,
          wrapperClassName: "flex",
          addHitTestRadius: p.addHitTestRadius
        };
      }

      case 'status': {
        const p = props as StatusChartProps;
        return {
          getChartAPI: getStatusChartAPI,
          chartKey: "statusChart",
          visibleSeriesStoreKey: "visibleSeriesStatus",
          setVisibleSeriesStoreKey: "setVisibleSeriesStatus",
          useAutoRange: false,
          useViewType: false,
          useAggregator: false,
          defaultHeight: "65px",
          showOverview: false,
          wrapperClassName: "flex",
          isFullscreen: p.isFullscreen,
          greasePumpData: p.greasePumpData,
          additionalChartOptions: {
            showStatus: p.showStatus
          }
        };
      }

      case 'timeline': {
        return {
          getChartAPI: getTimelineChartAPI,
          chartKey: "timelineChart",
          visibleSeriesStoreKey: "visibleSeriesTimeline",
          setVisibleSeriesStoreKey: "setVisibleSeriesTimeline",
          defaultHeight: 450,
          minHeight: "200px",
          showOverview: true,
          wrapperClassName: "flex"
        };
      }

      case 'fuel-burn': {
        const p = props as FuelBurnChartProps;
        return {
          getChartAPI: getFuelBurnChartAPI,
          chartKey: "fuelBurnChart",
          visibleSeriesStoreKey: "visibleSeriesFuelBurn",
          setVisibleSeriesStoreKey: "setVisibleSeriesFuelBurn",
          initialVisibleSeries: {
            FuelBurnId: true,
            SpeedId: true
          } as { [key: string]: boolean },
          useAggregator: false,
          defaultHeight: 500,
          showOverview: true,
          wrapperClassName: "flex",
          isFullscreen: p.isFullscreen
        };
      }

      default: {
        // Fallback for unknown chart types
        return {
          getChartAPI: getActivityChartAPI,
          chartKey: "activityChart",
          visibleSeriesStoreKey: "visibleSeriesActivity",
          setVisibleSeriesStoreKey: "setVisibleSeriesActivity",
          initialVisibleSeries: { VolumeDispensedId: true } as { [key: string]: boolean },
          defaultHeight: 500,
          showOverview: true,
          effects: [
            { type: 'changeChartType' as const, dependency: 'viewType' }
          ] as BaseChartEffect[]
        };
      }
    }
  };

  const config = getChartConfig();

  return (
    <BaseChart
      chartData={props.chartData}
      isSimpleChart={props.isSimpleChart}
      chartParentHeight={props.chartParentHeight}
      getChartAPI={config.getChartAPI}
      chartKey={config.chartKey}
      visibleSeriesStoreKey={config.visibleSeriesStoreKey}
      setVisibleSeriesStoreKey={config.setVisibleSeriesStoreKey}
      initialVisibleSeries={config.initialVisibleSeries}
      useAutoRange={config.useAutoRange}
      useViewType={config.useViewType}
      useAggregator={config.useAggregator}
      useIsSinglePressureChart={config.useIsSinglePressureChart}
      useIsThresholdsOn={config.useIsThresholdsOn}
      additionalChartOptions={config.additionalChartOptions}
      effects={config.effects}
      defaultHeight={config.defaultHeight}
      minHeight={config.minHeight}
      showOverview={config.showOverview}
      wrapperClassName={config.wrapperClassName}
      addHitTestRadius={config.addHitTestRadius}
      isFullscreen={config.isFullscreen}
      greasePumpData={config.greasePumpData}
      attachAnomalyBands={props.chartType !== 'status'}
    />
  );
}
